import { difference, equals, find, some, uniq, uniqueId, isEmpty } from 'lodash/fp';
import i18next from 'i18next';
import { TransformationType } from 'app/lib/types/server';
import { GENERIC_TRANSFORM_TYPE } from 'app/store/__generated__/types';
import {
    alias,
    columnExpression,
    from,
    identifier,
    rename,
    select,
    sql
} from 'app/lib/utils/sql_generator.js';
import {
    Converter,
    buildSqlFromExpressions
} from 'app/lib/codemirror/grammars/dapFormula/converter.js';
import { DATA_TYPE_MAP } from 'app/lib/codemirror/grammars/dapFormula/utils.js';
import { buildPayload, getRenames } from 'app/store/multiJoin/utils.js';
import { ERROR_NODE } from 'app/lib/constants/entityTypes';

export const Status = {
    STARTED: 'started',
    CREATED: 'created',
    FAILED: 'failed',
    PENDING: 'pending'
};

export const resolveIconForType = (t) => {
    switch (t) {
        case 'FormulaSheet':
            return 'function';
        case 'ExternalSheet':
            return 'op-replace';
        case 'TransformSheet':
            return 'flow';
        case 'SingleJoinSheet':
            return 'op-blend';
        case 'MultiJoinSheet':
            return 'op-blend';
        case 'DataSourceSheet':
            return 'dynamic-table';
        case 'UnionSheet':
            return 'op-union';
        case 'ExportJob':
            return 'deployment';
        case 'GROUP_NODE':
            return 'flow';
        case 'ImportJob':
        case 'DataLink':
            return 'connections';
        default: {
            return 'blank';
        }
    }
};

const getSourceNode = (file) => {
    const extension = file.dapFilecontents.file.extension;
    const dataSourceId = file.dapFilecontents.file.uuid;
    const name = file.dapFilecontents.file.name;
    const type = extension === 'imp' ? 'ImportJob' : 'DataLink';

    return {
        dataSourceId,
        name,
        type,
        dapFilePath: file.dapFilePath,
        hasConnectedNodes: false
    };
};

export const processFiles = (files) => {
    let id = 0;
    const nodes = [];
    const sourceNodes = [];
    const findSourceNode = (workbookId, name) => find({ name, workbookId }, nodes);

    files
        .filter(
            (file) =>
                file.dapFilecontents.file.extension === 'imp' ||
                file.dapFilecontents.file.extension === 'lnk'
        )
        .forEach((file) => {
            const node = getSourceNode(file);
            sourceNodes.push(node);
        });

    files
        .filter((file) => file.dapFilecontents.file.extension === 'wbk')
        .forEach((file) => {
            const workbookId = file.dapFilecontents.file.uuid;

            file.dapFilecontents.sheets.forEach((sheet) => {
                const sheetId = sheet.identifier;

                const node = {
                    id: String(id),
                    sheetId,
                    workbookId,
                    name: sheet.name,
                    sheetName: sheet.name,
                    type: sheet._type,
                    workbookName: file.name,
                    columns: sheet.columns,
                    kept: sheet.kept,
                    dapFilePath: `${file.dapFilePath}/${sheet.name}`,
                    dataSource: sheet.dataSource
                };

                nodes.push(node);

                id++;
            });
        });

    files
        .filter((file) => file.dapFilecontents.file.extension === 'wbk')
        .forEach((file) => {
            const workbookId = file.dapFilecontents.file.uuid;

            file.dapFilecontents.sheets.forEach((sheet) => {
                const sheetId = sheet.identifier;
                const node = find({ sheetId, workbookId }, nodes);

                switch (node.type) {
                    case 'DataSourceSheet': {
                        const sourceNode = find(
                            { dataSourceId: node.dataSource.uuid },
                            sourceNodes
                        );

                        if (sourceNode) {
                            const nodeId = String(id);
                            node.sources = [nodeId];
                            nodes.push({ id: nodeId, ...sourceNode });
                            id++;
                        }

                        break;
                    }
                    case 'ExternalSheet': {
                        const sourceSheetName = sheet.sourceSheet.sheet.name;
                        const sourceWorkbookId = sheet.sourceSheet.workbook.uuid;
                        const sourceNode = findSourceNode(sourceWorkbookId, sourceSheetName);

                        if (sourceNode) {
                            node.sources = [sourceNode.id];
                        } else {
                            console.warn(
                                'source for external sheet not found',
                                sourceWorkbookId,
                                sourceSheetName
                            );
                        }

                        break;
                    }

                    case 'SingleJoinSheet': {
                        const leftSheetName = sheet.definition.left.sheetName;
                        const rightSheetName = sheet.definition.right.sheetName;
                        const leftNode = findSourceNode(workbookId, leftSheetName);
                        const rightNode = findSourceNode(workbookId, rightSheetName);

                        node.sources = [];

                        if (leftNode) {
                            node.sources.push(leftNode.id);
                        } else {
                            console.warn('Source sheet not found', workbookId, leftSheetName);
                        }

                        if (rightNode) {
                            node.sources.push(rightNode.id);
                        } else {
                            console.warn('Source sheet not found', workbookId, rightSheetName);
                        }

                        node.definitions = [sheet.definition];

                        break;
                    }

                    case 'MultiJoinSheet': {
                        const sources = [];

                        sheet.definitions.forEach((definition) => {
                            const leftSheetName = definition.left.sheetName;
                            const rightSheetName = definition.right.sheetName;

                            const leftNode = findSourceNode(workbookId, leftSheetName);
                            const rightNode = findSourceNode(workbookId, rightSheetName);

                            if (leftNode) {
                                if (!sources.includes(leftNode.id)) {
                                    sources.push(leftNode.id);
                                }
                            } else {
                                console.warn('Source sheet not found', workbookId, leftSheetName);
                            }

                            if (rightNode) {
                                if (!sources.includes(rightNode.id)) {
                                    sources.push(rightNode.id);
                                }
                            } else {
                                console.warn('Source sheet not found', workbookId, rightSheetName);
                            }
                        });

                        node.definitions = sheet.definitions;
                        node.sources = sources;

                        break;
                    }

                    case 'UnionSheet': {
                        const sources = [];

                        sheet.sources.forEach((name) => {
                            const sourceNode = findSourceNode(workbookId, name);

                            if (sourceNode) {
                                sources.push(sourceNode.id);
                            } else {
                                console.warn('Source sheet not found', workbookId, name);
                            }
                        });

                        node.sources = sources;

                        break;
                    }

                    case 'FormulaSheet': {
                        const sources = [];

                        sheet.columns.forEach((column) => {
                            const sheetNameMatch = column.formula.match(/#(.*?)!/);

                            if (sheetNameMatch) {
                                const sourceNode = findSourceNode(workbookId, sheetNameMatch[1]);

                                if (sourceNode) {
                                    sources.push(sourceNode.id);
                                } else {
                                    console.warn(
                                        'Source sheet not found',
                                        workbookId,
                                        sheetNameMatch[1]
                                    );
                                }
                            }
                        });

                        node.sources = uniq(sources);

                        break;
                    }

                    case 'TransformSheet': {
                        const sourceSheetName = sheet.sourceSheetName;
                        const sourceNode = findSourceNode(workbookId, sourceSheetName);

                        if (sourceNode) {
                            node.sources = [sourceNode.id];
                        } else {
                            console.warn('Source sheet not found', workbookId, sourceSheetName);
                        }

                        break;
                    }

                    default:
                        break;
                }
            });
        });

    files
        .filter((file) => file.dapFilecontents.file.extension === 'exp')
        .forEach((file) => {
            const workbookId = file.dapFilecontents.file.uuid;
            const name = file.dapFilecontents.file.name;
            const type = 'ExportJob';
            const columns = file.dapFilecontents.columnMappings;
            const sheet = file.dapFilecontents.sheet;
            const sourceNode = findSourceNode(sheet.workbook.uuid, sheet.sheet.name);

            if (sourceNode) {
                const node = {
                    id: String(id),
                    workbookId,
                    name,
                    type,
                    columns,
                    sources: [sourceNode.id],
                    dapFilePath: file.dapFilePath
                };

                nodes.push(node);

                id++;
            } else {
                console.warn('source for export not found', sheet);
            }
        });

    return nodes;
};

export const calculateStats = (files) => {
    const feMetadata = {
        sheets: 0,
        datasources: 0,
        exports: 0
    };

    files.forEach((file) => {
        switch (file.dapFileType) {
            case 'wbk':
                feMetadata.sheets += file.dapFilecontents.sheets.length;
                break;
            case 'dst':
                feMetadata.datasources += 1;
                break;
            case 'exp':
                feMetadata.exports += 1;
                break;
            default:
                break;
        }
    });

    return feMetadata;
};

const translateDataSourceSheet = (sheet) => {
    const { name, columns } = sheet;
    const outputs = columns.map((column) => ({
        name: column.name,
        typeFamily: DATA_TYPE_MAP[column.type._type] ?? 'STRING'
    }));

    return {
        id: sheet.id,
        suggestedName: sheet.name,
        columns: sheet.columns,
        type: 'DraftModel',
        params: (projectUid) => ({
            endpoint: 'op',
            _type: 'europa.project.model.CreateDraftModel',
            columns: outputs,
            projectUid,
            name
        })
    };
};

const translateFormulaSheet = (sheet) => ({
    id: sheet.id,
    suggestedName: sheet.name,
    columns: sheet.columns,
    type: 'Formula',
    params: (projectUid, sources) => {
        const { name, columns } = sheet;
        const uniqueName = `${name} ${uniqueId()}`;
        const converter = new Converter(columns, sources);
        const expressions = columns.map((column) => converter.toSql(column.formula));

        if (some((expression) => expression.tableExp || expression.group, expressions)) {
            const uniqueName = `${name} ${uniqueId()}`;
            const columnNames = columns.map((column) => column.name);
            const sql = buildSqlFromExpressions(sources[0].viewName, expressions, columnNames);

            return {
                endpoint: 'op',
                _type: 'europa.project.CreateTransformation',
                transformationType: TransformationType.SQL,
                spaceUid: projectUid,
                suggestedName: uniqueName,
                payload: { sql }
            };
        }

        return {
            endpoint: 'op',
            _type: 'europa.project.CreateTransformation',
            transformationType: TransformationType.FORMULA,
            spaceUid: projectUid,
            insertAfter: sources[0].viewUid,
            suggestedName: uniqueName,
            payload: {
                columns: columns.map((column, i) => ({
                    name: column.name,
                    expression: expressions[i].exp,
                    isVisible: true
                }))
            }
        };
    }
});

const buildAssets = (sourceRefs) =>
    sourceRefs.map((sourceRef) => ({
        name: sourceRef.viewName,
        schema: sourceRef.columns.map((column) => ({
            name: column.name,
            type: DATA_TYPE_MAP[column.type._type] ?? 'STRING'
        }))
    }));

const collectIncludedColumns = (sheet) => {
    const includedColumns = {};

    sheet.definitions.forEach((definition) => {
        if (!includedColumns[definition.left.sheetName]) {
            includedColumns[definition.left.sheetName] = definition.left.includedColumns;
        }

        if (!includedColumns[definition.right.sheetName]) {
            includedColumns[definition.right.sheetName] = definition.right.includedColumns;
        }
    });

    return includedColumns;
};

const collectRenames = (columns, sourceRefs, includedColumns) => {
    const renames = {};
    let i = 0;

    sourceRefs.forEach((sourceRef) => {
        renames[sourceRef.suggestedName] = {};

        sourceRef.columns.forEach((column) => {
            if (includedColumns[sourceRef.suggestedName].includes(column.name)) {
                if (column.name !== columns[i].name) {
                    renames[sourceRef.suggestedName][column.name] = columns[i].name;
                }

                i++;
            }
        });
    });

    return renames;
};

const buildSource = (sourceRef, includes, renames) => {
    const sourceColumns = sourceRef.columns.map((column) => column.name);
    const excludes = difference(sourceColumns, includes);

    return {
        id: sourceRef.id,
        uid: sourceRef.viewUid,
        name: sourceRef.viewName,
        joinType: 'INNER',
        excludes,
        renames
    };
};

const buildCriteria = (sheet, sourceRefs) =>
    sheet.definitions.map((definition, i) => ({
        id: i,
        left: {
            id: sourceRefs.find(
                (sourceRef) => sourceRef.suggestedName === definition.left.sheetName
            ).id,
            column: definition.left.joinColumns[0]
        },
        right: {
            id: sourceRefs.find(
                (sourceRef) => sourceRef.suggestedName === definition.right.sheetName
            ).id,
            column: definition.right.joinColumns[0]
        }
    }));

const translateJoinSheet = (sheet) => ({
    id: sheet.id,
    suggestedName: sheet.name,
    columns: sheet.columns,
    type: 'Join',
    params: (projectUid, sourceRefs) => {
        const assets = buildAssets(sourceRefs);
        const includedColumns = collectIncludedColumns(sheet);
        const renames = collectRenames(sheet.columns, sourceRefs, includedColumns);
        const sources = sourceRefs.map((sourceRef) =>
            buildSource(
                sourceRef,
                includedColumns[sourceRef.suggestedName],
                renames[sourceRef.suggestedName]
            )
        );
        const criteria = buildCriteria(sheet, sourceRefs);
        const payload = buildPayload(assets, sources, criteria);

        return {
            endpoint: 'op',
            _type: 'europa.project.CreateTransformation',
            transformationType: TransformationType.MULTI_JOIN,
            spaceUid: projectUid,
            suggestedName: sheet.name,
            payload: {
                ...payload,
                suggestedViewName: sheet.name
            }
        };
    }
});

const translateUnionSheet = (sheet) => ({
    id: sheet.id,
    suggestedName: sheet.name,
    columns: sheet.columns,
    type: 'Union',
    params: (projectUid, sourceRefs) => {
        const { columns } = sheet;
        const inputDatasets = sourceRefs.map((sourceRef) => ({
            viewUid: sourceRef.viewUid,
            columns: sourceRef.columns.map((column) => ({
                name: column.name,
                type: DATA_TYPE_MAP[column.type._type]
            }))
        }));
        const outputColumns = columns.map((column) => ({
            name: column.name,
            type: DATA_TYPE_MAP[column.type._type]
        }));
        const payload = {
            inputDatasets,
            outputColumns,
            setOperator: 'UNION ALL'
        };

        return {
            endpoint: 'op',
            _type: 'europa.project.CreateTransformation',
            transformationType: TransformationType.UNION,
            spaceUid: projectUid,
            suggestedName: sheet.name,
            payload
        };
    }
});

const buildColumnList = (sourceColumns, outputColumns) => {
    const sourceNames = sourceColumns.map((c) => c.name);
    const outputNames = outputColumns.map((c) => c.name);

    if (equals(sourceNames, outputNames)) {
        return '*';
    }

    const columns = [];

    for (const outputColumn of outputColumns) {
        const sourceColumn = sourceColumns.find((c) => c.id === outputColumn.id);

        columns.push(alias(identifier(sourceColumn.name), identifier(outputColumn.name)));
    }

    return columns;
};

const translateExternalSheet = (sheet) => ({
    id: sheet.id,
    suggestedName: sheet.name,
    columns: sheet.columns,
    type: 'External',
    params: (projectUid, sourceRefs) => {
        const { name } = sheet;
        const uniqueName = `${name} ${uniqueId()}`;

        const query = sql(
            select(buildColumnList(sourceRefs[0].columns, sheet.columns)),
            from(identifier(sourceRefs[0].viewName))
        );

        return {
            endpoint: 'op',
            _type: 'europa.project.CreateTransformation',
            transformationType: TransformationType.SQL,
            spaceUid: projectUid,
            suggestedName: uniqueName,
            payload: { sql: query }
        };
    }
});

const translateExportJob = (sheet) => ({
    id: sheet.id,
    suggestedName: sheet.name,
    columns: sheet.columns,
    type: 'Export',
    params: (projectUid, sourceRefs) => {
        const { name } = sheet;
        const uniqueName = `${name} ${uniqueId()}`;

        const sourceColumns = sheet.columns.map((column) => ({ name: column.originalName }));
        const outputColumns = sheet.columns.map((column) => ({ name: column.name }));

        const query = sql(
            select(buildColumnList(sourceColumns, outputColumns)),
            from(identifier(sourceRefs[0].viewName))
        );

        return {
            endpoint: 'op',
            _type: 'europa.project.CreateTransformation',
            transformationType: TransformationType.SQL,
            spaceUid: projectUid,
            suggestedName: uniqueName,
            payload: { sql: query }
        };
    }
});

const createDummySqlNode = (sheet) => ({
    id: sheet.id,
    suggestedName: sheet.name,
    columns: sheet.columns,
    type: 'Transform',
    params: (projectUid, sourceRefs) => {
        const { name } = sheet;
        const uniqueName = `${name} ${uniqueId()}`;

        return {
            endpoint: 'op',
            _type: 'europa.project.CreateTransformation',
            transformationType: TransformationType.SQL,
            spaceUid: projectUid,
            suggestedName: uniqueName,
            payload: { sql: `SELECT * FROM ${identifier(sourceRefs[0].viewName)}` }
        };
    }
});

export const createErrorNode = (name, projectUid, sourceRefs, error) => {
    const inputs = sourceRefs.map((source) => ({
        uid: source.viewUid,
        table: source.viewName,
        passThrough: true,
        columns: [],
        dropped: []
    }));
    const columns = [];

    if (inputs.length > 1) {
        let allColumns = [];
        const renamesBySource = {};

        sourceRefs.forEach((sourceRef) => {
            const renames = getRenames(
                sourceRef.columns.map((column) => column.name),
                allColumns
            );

            renamesBySource[sourceRef.suggestedName] = renames;
            allColumns = [...allColumns, ...Object.values(renames)];
        });

        sourceRefs.forEach((sourceRef) => {
            const renames = renamesBySource[sourceRef.suggestedName];
            const renameClause = Object.keys(renames ?? {}).map((name) =>
                alias(identifier(name), identifier(renames[name]))
            );

            columns.push(
                columnExpression(identifier(sourceRef.viewName, '*'), rename(renameClause))
            );
        });
    } else {
        columns.push('*');
    }

    const sqlTemplate = sql(
        select(...columns),
        from(inputs.map((input) => identifier(input.table)))
    );

    return {
        endpoint: 'op',
        _type: 'europa.space.CreateTransformationNode',
        payload: {
            _type: GENERIC_TRANSFORM_TYPE,
            type: ERROR_NODE,
            model: { inputs, outputs: [], error },
            sqlTemplate,
            suggestedViewName: name
        },
        spaceUid: projectUid
    };
};

const createOutputModelNode = (model) => ({
    id: model.id,
    type: 'DraftModel',
    suggestedName: model.name,
    params: (projectUid, sources) => {
        const { targetTable } = model;
        const { viewUid, viewName } = sources[0];
        const outputs = sources[0].columns.map((column) => ({
            name: column.name,
            typeFamily: DATA_TYPE_MAP[column?.type?._type] ?? 'STRING'
        }));

        return {
            endpoint: 'op',
            _type: 'europa.project.model.CreateDraftModel',
            targetTable,
            tagMessage: i18next.t('components:TagModelDialog.tagMessage'),
            sourceNodeUid: viewUid,
            sourceNodeName: viewName,
            columns: outputs,
            projectUid,
            name: model.name
        };
    }
});

const translaters = {
    DataSourceSheet: translateDataSourceSheet,
    FormulaSheet: translateFormulaSheet,
    SingleJoinSheet: translateJoinSheet,
    MultiJoinSheet: translateJoinSheet,
    UnionSheet: translateUnionSheet,
    TransformSheet: createDummySqlNode,
    ExternalSheet: translateExternalSheet,
    ExportJob: translateExportJob,
    Model: createOutputModelNode
};

export const translateSheets = (sheets) => sheets.map((sheet) => translaters[sheet.type]?.(sheet));

export const parseFilePaths = (paths) => {
    if (isEmpty(paths)) {
        return [];
    }
    const root = {};

    paths.forEach(({ dapFilePath, id, workbookId }) => {
        const path = dapFilePath.startsWith('/') ? dapFilePath.slice(1) : dapFilePath;
        const parts = path.split('/');
        let current = root;

        parts.forEach((part, index) => {
            const isFile = index === parts.length - 1;
            if (!current[part]) {
                current[part] = {
                    isFile,
                    ...(isFile ? { id, workbookId } : { children: {} })
                };
            }
            if (!isFile) {
                current = current[part].children;
            }
        });
    });

    return root;
};
